import React from "react";
import PropTypes from "prop-types";
import usePageOffsets from "./usePageOffsets";

/**
 * Khung trang nổi (floating card) — style Geist (Vercel):
 *  - position:fixed lấp đầy vùng dưới navbar / sau sidebar
 *  - header cố định (flex-shrink-0), body cuộn (flex-1 overflow)
 *  - `geist-scope` áp font Geist cho toàn bộ module (chỉ trong đây, không đổi app)
 */
const PageShell = ({ header, children, footer }) => {
  const o = usePageOffsets();
  return (
    <div
      className="geist-scope tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden tw-bg-geist-bg tw-m-4 tw-rounded-geist-lg tw-shadow-geist-modal tw-border tw-border-solid tw-border-geist-border"
      style={{ position: "fixed", top: o.top, left: o.left, right: o.right, bottom: o.bottom, zIndex: 1 }}
    >
      {/* Không có header thì KHÔNG render div rỗng — trước đây vẫn dựng 1 thanh
          border-b trống, nhìn như đường viền thừa ở đầu trang. */}
      {header && (
        <div className="tw-flex-shrink-0 tw-bg-geist-bg tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-z-30">{header}</div>
      )}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto tw-bg-geist-bg-2">{children}</div>
      {footer && (
        <div className="tw-flex-shrink-0 tw-bg-geist-bg tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">{footer}</div>
      )}
    </div>
  );
};

PageShell.propTypes = {
  header: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
};

// ── Typography Geist: tối giản, tương phản cao, không gradient ──

// Heading khối (drawer/section).
export const SectionHeading = ({ children, className = "" }) => (
  <h3 className={`tw-text-base tw-font-semibold tw-text-geist-fg tw-tracking-tight tw-m-0 ${className}`}>
    {children}
  </h3>
);

SectionHeading.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

// Label trường — `*` (required) màu error.
// `hint`: a short explanation behind an (i) on hover/focus, so it does not take a row of its own.
export const FieldLabel = ({ children, className = "", required = false, hint = "" }) => (
  <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-geist-fg-2 tw-mb-1.5 ${className}`}>
    <span>
      {children}
      {required && <span className="tw-text-geist-error"> *</span>}
    </span>
    {hint && (
      <i className="bi bi-info-circle tw-text-[12px] tw-text-geist-fg-3 tw-cursor-help" title={hint} aria-label={hint}
        role="img" tabIndex={0} />
    )}
  </span>
);

FieldLabel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  required: PropTypes.bool,
  hint: PropTypes.string,
};

// Tiêu đề trang (h1) + subtitle.
export const PageTitle = ({ children, subtitle }) => (
  <div>
    <h1 className="tw-text-[22px] tw-font-semibold tw-tracking-tight tw-text-geist-fg tw-m-0">
      {children}
    </h1>
    {subtitle && <p className="tw-text-[13px] tw-text-geist-fg-3 tw-m-0 tw-mt-0.5">{subtitle}</p>}
  </div>
);

PageTitle.propTypes = {
  children: PropTypes.node,
  subtitle: PropTypes.node,
};

// Alias tương thích ngược (tên cũ) — để import cũ không vỡ trong lúc chuyển đổi.
export const GradientHeading = SectionHeading;
export const GradientLabel = FieldLabel;
export const GradientTitle = PageTitle;

export default PageShell;
